package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.events.EventListenerManager
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.overlay.AdvancedTooltip
import com.varabyte.kobweb.silk.components.overlay.OpenClose
import com.varabyte.kobweb.silk.components.overlay.OpenClosePopupStrategy
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.PopupPlacementStrategy
import com.varabyte.kobweb.silk.components.overlay.TooltipTextContainerStyle
import com.varabyte.kobweb.silk.components.overlay.TooltipVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Br
import org.w3c.dom.HTMLElement
import kotlin.random.Random

private const val INSTANT_TOOLTIP_TRANSITION_MS = 1

/**
 * Shares the warmed-tooltip state across a local toolbar or other neighbouring trigger group.
 *
 * **Warm is one-way, and for the default group that means one-way for the session.** Nothing clears
 * [warmed] — `reset()` deliberately does not touch it — so the first tooltip a visitor waits
 * [Motion.FastMs] for is the only one they ever wait for. That reads like an oversight and is not:
 * the delay exists so a cursor crossing a toolbar does not flash a tooltip it was only passing, and
 * a reader who has seen one tooltip has already told us they are reading tooltips. A cool-down would
 * make the second visit to a control slower than the fifth, which is the wrong way round for
 * something reached dozens of times an hour.
 *
 * Pass your own instance to scope that decision to one toolbar instead of the whole page.
 */
class LTooltipGroupState {
    internal var warmed: Boolean = false
}

/** The page-wide group. Warms on the first tooltip anywhere and stays warm — see [LTooltipGroupState]. */
private val DefaultTooltipGroupState = LTooltipGroupState()

val LTooltipStyle = CssStyle.base {
    Modifier
        .setVariable(TooltipVars.BackgroundColor, colorMode.toSitePalette().surfaceRaised)
        .setVariable(TooltipVars.Color, colorMode.toSitePalette().textPrimary)
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .color(colorMode.toSitePalette().textPrimary)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .borderRadius(Radius.Control)
        // A tooltip is a floating layer, so it gets the floating shadow — the hairline alone left
        // it sitting *in* whatever it was covering, which is worst over a table or a code block
        // where the border it draws is the same 1px the content underneath is already drawing.
        .elevation(Elevation.Floating)
        .padding(topBottom = Space.XS, leftRight = Space.SM)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
        .styleModifier {
            // `AdvancedTooltip` sizes the box to its content with no floor and no ceiling. Against
            // the right edge of the viewport that shrank the box down to its narrowest wrapped
            // line instead of the width its text actually wants — measured at 56px, wrapped into a
            // column, for a tooltip whose text would sit on one line anywhere else on the page.
            property("width", "max-content")
            property("max-width", "min(280px, calc(100vw - 16px))")
        }
}

/**
 * A tooltip whose body is composition rather than a sentence.
 *
 * The text overload below is still the one almost every call site wants. This exists because two of
 * them do not: a control that states its keyboard chord has to draw it as [LChord] — one `<kbd>` per
 * key, with the connective a screen reader needs between them — and a chord flattened back into
 * `"Refresh the queue (⌘ R)"` to fit a `String` parameter is exactly the key caps this design system
 * spent [LChord] on, thrown away at the last step.
 *
 * The body is wrapped in Silk's own `TooltipTextContainerStyle`, which is what the text overload
 * gets. Without it a composed tooltip is 5px tighter than every text one on the same toolbar, and
 * the difference shows the moment the two sit side by side.
 */
@Composable
fun LTooltip(
    modifier: Modifier = Modifier,
    placement: PopupPlacement = PopupPlacement.Top,
    groupState: LTooltipGroupState = DefaultTooltipGroupState,
    tooltipContent: @Composable () -> Unit,
    content: @Composable (triggerModifier: Modifier) -> Unit,
) {
    val tooltipId = remember { "lingvo-tooltip-${Random.nextInt(1_000_000)}" }
    val reducedMotion = rememberReducedMotion()
    var instant by remember { mutableStateOf(false) }
    // Which way the tooltip is actually drawn, which may not be [placement]: a trigger near the
    // top or bottom edge of the viewport is flipped to the opposite side of the pair before the
    // tooltip opens (see [flippedForViewport]) — measured on the editor's overflow trigger, whose
    // `Top` tooltip opened at `top: -79px` because nothing ever checked whether there was 79px of
    // viewport above it to open into.
    var effectivePlacement by remember(placement) { mutableStateOf(placement) }
    val placementStrategy = remember(effectivePlacement) { PopupPlacementStrategy.of(effectivePlacement, 8) }
    val openCloseStrategy = remember(groupState, placement, tooltipId) {
        TooltipOpenCloseStrategy(
            groupState = groupState,
            basePlacement = placement,
            tooltipId = tooltipId,
            onOpening = { nextInstant, resolvedPlacement ->
                instant = nextInstant
                effectivePlacement = resolvedPlacement
            },
        )
    }
    content(Modifier.attr("aria-describedby", tooltipId))
    AdvancedTooltip(
        target = ElementTarget.PreviousSibling,
        modifier = LTooltipStyle.toModifier()
            .attr("id", tooltipId)
            .attr("role", "tooltip")
            .attr("data-tooltip-motion", "interruptible")
            .attr("data-tooltip-instant", instant.toString())
            .styleModifier {
                // `--l-tooltip-clamp-x` is the horizontal correction `TooltipOpenCloseStrategy`
                // measures in after the box has a real size — see its `clampHorizontally`. Riding
                // along with the entrance transform rather than a competing `left`/`margin` means
                // it never fights the translate the motion system already owns here.
                property("transform", "translate(var(--l-tooltip-clamp-x, 0px), 0) scale(1)")
                property("transform-origin", tooltipTransformOrigin(effectivePlacement))
            }
            // Keep this inline so it wins over Kobweb's built-in opacity-only PopupStyle without
            // using !important; the global reduced-motion duration can still override it.
            // `EaseInOut`, because this one declaration serves both directions. The system's third
            // stated rule is that exits use `EaseIn` and entrances never do, and a single
            // `transition` cannot say two things — it was `EaseOut`, which made the tooltip's
            // departure accelerate away rather than settle out of the way.
            .transitionSurface(
                durationMs = if (instant) INSTANT_TOOLTIP_TRANSITION_MS else Motion.FastMs,
                easing = Motion.EaseInOut,
            )
            .then(modifier),
        hiddenModifier = Modifier.styleModifier {
            property("transform", tooltipHiddenTransform(effectivePlacement, reducedMotion))
        },
        openCloseStrategy = openCloseStrategy,
        placementStrategy = placementStrategy,
        // The strategy owns the asymmetric hover/focus timing. These remain zero so Kobweb does not
        // add a second delay after a request, and so hiding can reverse without replacing the node.
        showDelayMs = Motion.InstantMs,
        hideDelayMs = Motion.InstantMs,
    ) {
        Column(TooltipTextContainerStyle.toModifier()) { tooltipContent() }
    }
}

@Composable
fun LTooltip(
    text: String,
    modifier: Modifier = Modifier,
    placement: PopupPlacement = PopupPlacement.Top,
    groupState: LTooltipGroupState = DefaultTooltipGroupState,
    content: @Composable (triggerModifier: Modifier) -> Unit,
) {
    LTooltip(
        modifier = modifier,
        placement = placement,
        groupState = groupState,
        // Silk's own text body, reproduced rather than delegated to: routing this through
        // `AdvancedTooltip(text = …)` would have been a second copy of the forty lines of chrome
        // above, and the two would have drifted the first time one of them was tuned. A blank line
        // in the source is a `<br>`, which is the behaviour the text overload has always had.
        tooltipContent = {
            text.split("\n").forEach { line -> if (line.isNotEmpty()) SpanText(line) else Br() }
        },
        content = content,
    )
}

private class TooltipOpenCloseStrategy(
    private val groupState: LTooltipGroupState,
    private val basePlacement: PopupPlacement,
    private val tooltipId: String,
    private val onOpening: (instant: Boolean, resolvedPlacement: PopupPlacement) -> Unit,
) : OpenClosePopupStrategy() {
    private var manager: EventListenerManager? = null
    private var hoverTimer: Int? = null
    private var hovered = false
    private var focused = false
    private var targetElement: HTMLElement? = null

    override fun init(targetElement: HTMLElement) {
        this.targetElement = targetElement
        manager = EventListenerManager(targetElement).apply {
            addEventListener("mouseenter") {
                hovered = true
                requestHoverOpen()
            }
            addEventListener("mouseleave") {
                hovered = false
                cancelHoverTimer()
                if (!focused) emitRequest(OpenClose.CLOSE)
            }
            addEventListener("focusin") {
                focused = true
                cancelHoverTimer()
                requestOpen(instant = true)
            }
            addEventListener("focusout") {
                focused = false
                if (document.hasFocus() && !hovered) emitRequest(OpenClose.CLOSE)
            }
        }

        if (targetElement.contains(document.activeElement)) {
            focused = true
            requestOpen(instant = true)
        } else if (targetElement.matches(":hover")) {
            hovered = true
            requestHoverOpen()
        }
    }

    private fun requestHoverOpen() {
        cancelHoverTimer()
        val delayMs = tooltipHoverDelayMs(groupState.warmed)
        if (delayMs == Motion.InstantMs) {
            requestOpen(instant = true)
        } else {
            hoverTimer = window.setTimeout({
                hoverTimer = null
                if (hovered) requestOpen(instant = false)
            }, delayMs)
        }
    }

    private fun requestOpen(instant: Boolean) {
        val resolved = targetElement?.let { flippedForViewport(basePlacement, it.getBoundingClientRect()) }
            ?: basePlacement
        onOpening(instant, resolved)
        groupState.warmed = true
        emitRequest(OpenClose.OPEN)
        clampHorizontally(tooltipId)
    }

    private fun cancelHoverTimer() {
        hoverTimer?.let(window::clearTimeout)
        hoverTimer = null
    }

    override fun reset() {
        cancelHoverTimer()
        manager?.clearAllListeners()
        manager = null
        hovered = false
        focused = false
        targetElement = null
    }
}

/**
 * Flips a `Top*`/`Bottom*` placement to its mirror when the trigger has no room on that side.
 *
 * The tooltip's own height is not known yet — it has not rendered — so this uses
 * [ESTIMATED_TOOLTIP_BLOCK_PX], a conservative stand-in for one line of text plus its padding and
 * the 8px gap `PopupPlacementStrategy` opens with. Good enough to catch the case that actually
 * ships: a toolbar trigger sitting inside the first ~60px of the viewport, or the last ~60px of it.
 */
internal fun flippedForViewport(
    placement: PopupPlacement,
    triggerRect: org.w3c.dom.DOMRect,
    viewportHeight: Double = window.innerHeight.toDouble(),
): PopupPlacement {
    val opensUpward = placement == PopupPlacement.Top ||
        placement == PopupPlacement.TopLeft ||
        placement == PopupPlacement.TopRight
    val opensDownward = placement == PopupPlacement.Bottom ||
        placement == PopupPlacement.BottomLeft ||
        placement == PopupPlacement.BottomRight
    return when {
        opensUpward && triggerRect.top < ESTIMATED_TOOLTIP_BLOCK_PX -> when (placement) {
            PopupPlacement.Top -> PopupPlacement.Bottom
            PopupPlacement.TopLeft -> PopupPlacement.BottomLeft
            PopupPlacement.TopRight -> PopupPlacement.BottomRight
            else -> placement
        }
        opensDownward && (viewportHeight - triggerRect.bottom) < ESTIMATED_TOOLTIP_BLOCK_PX -> when (placement) {
            PopupPlacement.Bottom -> PopupPlacement.Top
            PopupPlacement.BottomLeft -> PopupPlacement.TopLeft
            PopupPlacement.BottomRight -> PopupPlacement.TopRight
            else -> placement
        }
        else -> placement
    }
}

private const val ESTIMATED_TOOLTIP_BLOCK_PX = 48.0
private const val VIEWPORT_EDGE_MARGIN_PX = 8.0

/**
 * Pulls the rendered tooltip back inside the viewport's inline edges.
 *
 * Placement and flip pick a *side* relative to the trigger; neither one clamps against the
 * viewport's left/right edges, so a trigger near either edge still opened a tooltip that ran off
 * it. Reapplied across several frames rather than once: a flip
 * ([flippedForViewport]/[TooltipOpenCloseStrategy.onOpening]) changes `effectivePlacement`, which
 * changes the `PopupPlacementStrategy` instance `LTooltip` hands `AdvancedTooltip`, and that can
 * replace the popup's DOM node with a fresh one after this correction has already landed on the
 * old one — so this keeps re-measuring and re-setting the variable on whatever node currently
 * holds [tooltipId] for a short window, rather than trusting the first frame it finds one.
 */
private fun clampHorizontally(tooltipId: String, framesRemaining: Int = CLAMP_RETRY_FRAMES) {
    window.requestAnimationFrame {
        (document.getElementById(tooltipId) as? HTMLElement)?.let { tooltip ->
            // The correction has to be computed from the box's *natural* (unclamped) position,
            // not from wherever the previous frame's correction already moved it to. Reading
            // `getBoundingClientRect()` after a prior correction returns the already-shifted box,
            // and treating that shifted position as the new baseline is what let a first
            // correction of -83px get *replaced* by a second, independently-computed -50px
            // instead of accumulating with it — leaving the box 50px short of the edge instead of
            // 133px inside it.
            val alreadyApplied = tooltip.style.getPropertyValue("--l-tooltip-clamp-x")
                .removeSuffix("px")
                .toDoubleOrNull()
                ?: 0.0
            val rect = tooltip.getBoundingClientRect()
            val naturalRight = rect.right - alreadyApplied
            val naturalLeft = rect.left - alreadyApplied
            val overflowRight = naturalRight - (window.innerWidth - VIEWPORT_EDGE_MARGIN_PX)
            val overflowLeft = VIEWPORT_EDGE_MARGIN_PX - naturalLeft
            val correction = when {
                overflowRight > 0 -> -overflowRight
                overflowLeft > 0 -> overflowLeft
                else -> 0.0
            }
            tooltip.style.setProperty("--l-tooltip-clamp-x", "${correction}px")
        }
        if (framesRemaining > 0) clampHorizontally(tooltipId, framesRemaining - 1)
    }
}

private const val CLAMP_RETRY_FRAMES = 15

internal fun tooltipHoverDelayMs(warmed: Boolean): Int =
    if (warmed) Motion.InstantMs else Motion.FastMs

internal fun tooltipTransformOrigin(placement: PopupPlacement): String = when (placement) {
    PopupPlacement.TopLeft -> "bottom left"
    PopupPlacement.Top -> "bottom center"
    PopupPlacement.TopRight -> "bottom right"
    PopupPlacement.LeftTop -> "top right"
    PopupPlacement.Left -> "center right"
    PopupPlacement.LeftBottom -> "bottom right"
    PopupPlacement.RightTop -> "top left"
    PopupPlacement.Right -> "center left"
    PopupPlacement.RightBottom -> "bottom left"
    PopupPlacement.BottomLeft -> "top left"
    PopupPlacement.Bottom -> "top center"
    PopupPlacement.BottomRight -> "top right"
}

/**
 * The `translate()` a hidden tooltip sits at before it enters.
 *
 * `var(--l-tooltip-clamp-x, 0px)` rides along on the X axis of every branch, including the
 * `reducedMotion` one: the visible state ([LTooltipStyle]'s inline `transform`) puts the same
 * variable in the same axis, and if this left it out of even one branch the tooltip would snap
 * sideways by the clamp correction the instant it crossed from hidden to visible.
 */
internal fun tooltipHiddenTransform(
    placement: PopupPlacement,
    reducedMotion: Boolean = false,
): String = if (reducedMotion) {
    "translate(var(--l-tooltip-clamp-x, 0px), 0) scale(1)"
} else when (placement) {
    PopupPlacement.TopLeft, PopupPlacement.Top, PopupPlacement.TopRight ->
        "translate(var(--l-tooltip-clamp-x, 0px), ${Motion.TooltipTravelPx}px) scale(${Motion.PopoverEnterScale})"
    PopupPlacement.LeftTop, PopupPlacement.Left, PopupPlacement.LeftBottom ->
        "translateX(calc(var(--l-tooltip-clamp-x, 0px) + ${Motion.TooltipTravelPx}px)) " +
            "scale(${Motion.PopoverEnterScale})"
    PopupPlacement.RightTop, PopupPlacement.Right, PopupPlacement.RightBottom ->
        "translateX(calc(var(--l-tooltip-clamp-x, 0px) - ${Motion.TooltipTravelPx}px)) " +
            "scale(${Motion.PopoverEnterScale})"
    PopupPlacement.BottomLeft, PopupPlacement.Bottom, PopupPlacement.BottomRight ->
        "translate(var(--l-tooltip-clamp-x, 0px), -${Motion.TooltipTravelPx}px) scale(${Motion.PopoverEnterScale})"
}
